import { Plus, Trash2, Settings2, Sparkles, Pencil, Check, X, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/api/trpc';
import { toast } from 'sonner';

interface PresetManagerProps {
  deploymentId?: string; // Short deployment ID (e.g., "misqrfl6vid3r")
  pluginDeploymentId?: string; // Full deployment ID (e.g., "plugin_deployment_09c6ed7847d4")
  deploymentPresets: Record<string, any>; // Original presets from manifest
  currentConfig: any;
  onPresetSelect: (
    config: any,
    presetInfo: { id: string; name: string; type: 'deployment' | 'custom' },
  ) => void;
  selectedPresetId?: string | null; // Currently selected preset ID
  onPresetUpdate?: (presetId: string, config: any) => Promise<void>; // Callback to update preset
  onPresetFork?: (originalPresetName: string, config: any) => Promise<{ id: string; name: string }>; // Callback to fork official preset
}

/**
 * Preset Manager Component
 * Manages both deployment presets and custom account presets
 * Integrates with AI for preset generation
 */
export function PresetManager({
  deploymentId,
  pluginDeploymentId,
  deploymentPresets,
  currentConfig,
  onPresetSelect,
  selectedPresetId: externalSelectedPresetId,
  onPresetUpdate,
  onPresetFork,
}: PresetManagerProps) {
  const { t } = useTranslation('storefront');
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [selectedPresetValue, setSelectedPresetValue] = useState<string>(externalSelectedPresetId || '');
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [presetToDelete, setPresetToDelete] = useState<{ id: string; name: string } | null>(null);

  // Fetch custom presets for this deployment
  const { data: customPresets, refetch } = trpc.pluginPresets.list.useQuery(
    { pluginDeploymentId },
    { enabled: !!pluginDeploymentId },
  );

  // Debug logging
  console.log('[PresetManager] Query state:', {
    pluginDeploymentId,
    enabled: !!pluginDeploymentId,
    customPresetsCount: customPresets?.length ?? 'loading',
    customPresets: customPresets?.map((p: any) => ({ id: p.id, name: p.name })),
  });

  // Mutations
  const createPreset = trpc.pluginPresets.create.useMutation();
  const updatePreset = trpc.pluginPresets.update.useMutation();
  const deletePreset = trpc.pluginPresets.delete.useMutation();

  // Sync selectedPresetValue with external preset ID
  useEffect(() => {
    if (externalSelectedPresetId) {
      setSelectedPresetValue(externalSelectedPresetId);
    }
  }, [externalSelectedPresetId]);

  const handleSaveAsPreset = async () => {
    if (!presetName.trim()) {
      toast.error(t('configEditor.preset.emptyName'));
      return;
    }

    try {
      await createPreset.mutateAsync({
        pluginDeploymentId,
        name: presetName,
        description: presetDescription,
        config: currentConfig,
        isAiGenerated: false,
      });

      toast.success(t('configEditor.preset.presetSaved'));
      setPresetName('');
      setPresetDescription('');
      refetch();
    } catch (error: any) {
      toast.error(error.message || t('configEditor.preset.failedSave'));
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    try {
      await deletePreset.mutateAsync({ presetId });
      toast.success(t('configEditor.preset.presetDeleted'));
      refetch();
    } catch (error: any) {
      toast.error(error.message || t('configEditor.preset.failedDelete'));
    }
  };

  const handleUpdatePreset = async (presetId: string, config: any) => {
    try {
      await updatePreset.mutateAsync({
        presetId,
        config,
      });
      toast.success(t('configEditor.preset.presetUpdated'));
      await refetch();
    } catch (error: any) {
      toast.error(error.message || t('configEditor.preset.failedUpdate'));
      throw error;
    }
  };

  const handleForkPreset = async (originalPresetName: string, config: any) => {
    try {
      const forkedName = `${originalPresetName} (Custom)`;
      const result = await createPreset.mutateAsync({
        pluginDeploymentId,
        name: forkedName,
        description: `Forked from official preset: ${originalPresetName}`,
        config,
        isAiGenerated: false,
      });

      toast.success(t('configEditor.preset.createdCustom', { name: forkedName }));
      await refetch();

      return { id: `custom:${result.id}`, name: forkedName };
    } catch (error: any) {
      toast.error(error.message || t('configEditor.preset.failedFork'));
      throw error;
    }
  };

  const handleRenamePreset = async (presetId: string, newName: string) => {
    if (!newName.trim()) {
      toast.error(t('configEditor.preset.emptyNameError'));
      return;
    }

    try {
      await updatePreset.mutateAsync({
        presetId,
        name: newName,
      });

      toast.success(t('configEditor.preset.presetRenamed'));
      setEditingPresetId(null);
      setEditingName('');
      await refetch();
    } catch (error: any) {
      toast.error(error.message || t('configEditor.preset.failedRename'));
    }
  };

  const startEditing = (presetId: string, currentName: string) => {
    setEditingPresetId(presetId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingPresetId(null);
    setEditingName('');
  };

  return (
    <div className="flex items-center gap-2">
      {/* Preset Selector */}
      <Select
        value={selectedPresetValue}
        onValueChange={(value) => {
          setSelectedPresetValue(value);
          if (value.startsWith('deployment:')) {
            const presetName = value.replace('deployment:', '');
            onPresetSelect(deploymentPresets[presetName], {
              id: value,
              name: presetName,
              type: 'deployment',
            });
          } else if (value.startsWith('custom:')) {
            const preset = customPresets?.find((p) => p.id === value.replace('custom:', ''));
            if (preset) {
              onPresetSelect(preset.config, {
                id: value,
                name: preset.name,
                type: 'custom',
              });
            }
          }
        }}
      >
        <SelectTrigger className="h-8 w-[180px] overflow-hidden text-xs font-medium shadow-sm">
          <SelectValue placeholder={t('configEditor.preset.selectPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {/* Deployment Presets */}
          {Object.keys(deploymentPresets).length > 0 && (
            <>
              <div className="text-muted-foreground px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider">
                {t('configEditor.preset.officialPresets')}
              </div>
              {Object.keys(deploymentPresets).map((presetName) => (
                <SelectItem key={presetName} value={`deployment:${presetName}`} className="text-xs">
                  <span className="truncate" title={presetName}>{presetName}</span>
                </SelectItem>
              ))}
            </>
          )}

          {/* Custom Presets */}
          {customPresets && customPresets.length > 0 && (
            <>
              <div className="text-muted-foreground px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider">
                {t('configEditor.preset.myPresets')}
              </div>
              {customPresets.map((preset) => (
                <div key={preset.id} className="relative">
                  <SelectItem value={`custom:${preset.id}`} className="pr-8 text-xs">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium" title={preset.name}>{preset.name}</span>
                      {preset.isAiGenerated && (
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                          <Sparkles className="h-2.5 w-2.5" />
                          AI
                        </span>
                      )}
                    </div>
                  </SelectItem>
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPresetToDelete({ id: preset.id, name: preset.name });
                    }}
                    className="text-muted-foreground hover:text-destructive absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 transition-colors hover:bg-red-50"
                    title={`Delete ${preset.name}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </>
          )}
        </SelectContent>
      </Select>

      {/* Manage Presets Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowManageDialog(true)}
        className="h-8 px-2.5 shadow-sm"
      >
        <Settings2 className="h-3.5 w-3.5" />
      </Button>

      {/* Manage Presets Dialog (combines Save + Manage) */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('configEditor.preset.managePresets')}</DialogTitle>
            <DialogDescription>{t('configEditor.preset.manageDescription')}</DialogDescription>
          </DialogHeader>

          {/* Save New Preset Section */}
          <div className="border-border bg-muted/50 rounded-lg border p-3">
            <h4 className="mb-2 text-sm font-semibold">{t('configEditor.preset.saveCurrentConfig')}</h4>
            <div className="space-y-2">
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder={t('configEditor.preset.presetNamePlaceholder')}
                className="h-8 text-xs"
              />
              <Textarea
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                placeholder={t('configEditor.preset.descriptionPlaceholder')}
                rows={2}
                className="text-xs"
              />
              <Button
                onClick={handleSaveAsPreset}
                disabled={createPreset.isPending || !presetName.trim()}
                size="sm"
                className="w-full"
              >
                <Plus className="mr-1 h-3 w-3" />
                {createPreset.isPending ? t('configEditor.preset.saving') : t('configEditor.preset.saveAsPreset')}
              </Button>
            </div>
          </div>

          {/* Existing Presets Section */}
          <div>
            <h4 className="mb-2 text-sm font-semibold">{t('configEditor.preset.yourPresets')}</h4>
            <div className="max-h-[300px] space-y-2 overflow-y-auto px-1 -mx-1">
              {customPresets && customPresets.length > 0 ? (
                customPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className="border-border flex items-center justify-between rounded-md border p-3"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {editingPresetId === preset.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="h-7 text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleRenamePreset(preset.id, editingName);
                                } else if (e.key === 'Escape') {
                                  cancelEditing();
                                }
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRenamePreset(preset.id, editingName)}
                              disabled={updatePreset.isPending}
                              className="h-7 w-7 p-0"
                            >
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={cancelEditing} className="h-7 w-7 p-0">
                              <X className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <h4 className="font-medium">{preset.name}</h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditing(preset.id, preset.name)}
                              className="h-6 w-6 p-0"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {preset.isAiGenerated && (
                          <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                            {t('configEditor.preset.aiGenerated')}
                          </span>
                        )}
                      </div>
                      {preset.description && (
                        <p className="text-muted-foreground mt-1 text-sm">{preset.description}</p>
                      )}
                      <p className="text-muted-foreground mt-1 text-xs">
                        {t('configEditor.preset.created')} {new Date(preset.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const presetValue = `custom:${preset.id}`;
                          setSelectedPresetValue(presetValue);
                          onPresetSelect(preset.config, {
                            id: presetValue,
                            name: preset.name,
                            type: 'custom',
                          });
                          setShowManageDialog(false);
                          toast.success(t('configEditor.preset.presetLoaded'));
                        }}
                      >
                        {t('configEditor.preset.load')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeletePreset(preset.id)}
                        disabled={deletePreset.isPending}
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center text-sm">{t('configEditor.preset.noCustomPresets')}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageDialog(false)}>
              {t('configEditor.preset.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Preset Confirmation Dialog */}
      <AlertDialog open={!!presetToDelete} onOpenChange={(open) => !open && setPresetToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('configEditor.preset.deletePreset')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('configEditor.preset.deleteConfirm', { name: presetToDelete?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePreset.isPending}>{t('configEditor.preset.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (presetToDelete) {
                  await handleDeletePreset(presetToDelete.id);
                  // If the deleted preset was selected, clear the selection
                  if (selectedPresetValue === `custom:${presetToDelete.id}`) {
                    setSelectedPresetValue('');
                  }
                  setPresetToDelete(null);
                }
              }}
              disabled={deletePreset.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePreset.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('configEditor.preset.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('configEditor.preset.deletePreset')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
